/**
 * DirectSupportBubble Component
 * 
 * Frontend-only support launcher that opens native messaging apps
 * - Floating bubble button (bottom-right)
 * - Opens panel with three action buttons: SMS, WhatsApp, Email
 * - No backend dependencies
 * - Fully responsive and accessible
 */

import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, MessageSquare, Phone, Mail } from "lucide-react";

// Contact details
const PHONE_NUMBER = "+16195130013";
const PHONE_NUMBER_CLEAN = "16195130013"; // Without + for WhatsApp
const PRIMARY_EMAIL = "contact@thetacogarage.com";

/**
 * Generate default message content
 * Can be extended in the future to accept product context
 */
const generateDefaultMessage = (productContext = null) => {
  let message = "Hi! I have a question about:\n";
  message += "• Product:\n";
  message += "• Vehicle / Year:\n";
  message += "• Order # (if any):";
  
  // Future: inject product context if provided
  if (productContext) {
    message = `Hi! I have a question about:\n`;
    message += `• Product: ${productContext.name || ""}\n`;
    message += `• Vehicle / Year:\n`;
    message += `• Order # (if any):`;
  }
  
  return message;
};

const DirectSupportBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  /**
   * Close panel when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        !event.target.closest('[data-support-bubble]')
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  /**
   * Handle SMS action
   */
  const handleSMS = () => {
    const message = encodeURIComponent(generateDefaultMessage());
    const smsLink = `sms:${PHONE_NUMBER}?body=${message}`;
    window.location.href = smsLink;
    setIsOpen(false);
  };

  /**
   * Handle WhatsApp action
   */
  const handleWhatsApp = () => {
    const message = encodeURIComponent(generateDefaultMessage());
    const whatsappLink = `https://wa.me/${PHONE_NUMBER_CLEAN}?text=${message}`;
    window.open(whatsappLink, "_blank");
    setIsOpen(false);
  };

  /**
   * Handle Email action
   */
  const handleEmail = () => {
    const message = encodeURIComponent(generateDefaultMessage());
    const emailLink = `mailto:${PRIMARY_EMAIL}?subject=${encodeURIComponent("Customer Inquiry")}&body=${message}`;
    window.location.href = emailLink;
    setIsOpen(false);
  };

  /**
   * Toggle panel
   */
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Floating Bubble Button */}
      <button
        data-support-bubble
        onClick={togglePanel}
        className="
          fixed 
          bottom-20
          right-4
          sm:bottom-6 
          sm:right-6 
          z-50
          bg-brandred 
          hover:bg-[#E63B3B] 
          text-white 
          rounded-full 
          p-3
          sm:p-4 
          shadow-lg
          transition-all 
          duration-300 
          hover:scale-110 
          active:scale-95
          focus:outline-none 
          focus:ring-4 
          focus:ring-brandred/50
          w-14 
          h-14
          sm:w-16 
          sm:h-16 
          md:w-20 
          md:h-20
          flex 
          items-center 
          justify-center
        "
        aria-label={isOpen ? "Close support options" : "Open support options"}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <X className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" strokeWidth={2} />
        ) : (
          <MessageCircle className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" strokeWidth={2} />
        )}
      </button>

      {/* Support Panel */}
      {isOpen && (
        <>
          {/* Backdrop (mobile only) */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Panel */}
          <div
            ref={panelRef}
            className="
              support-panel-container
              fixed 
              bottom-36
              right-4
              sm:bottom-24 
              sm:right-6 
              z-50
              w-[calc(100vw-2rem)]
              sm:w-[calc(100vw-3rem)] 
              max-w-sm
              rounded-2xl 
              shadow-2xl 
              border 
              border-gray-200
              md:bottom-32
              overflow-hidden
            "
            style={{
              backgroundColor: '#ffffff',
              opacity: 1,
              background: '#ffffff',
            }}
            role="dialog"
            aria-labelledby="support-panel-header"
            aria-modal="true"
          >
            {/* Header */}
            <div className="bg-brandred text-white p-4 flex items-center justify-between">
              <h2 id="support-panel-header" className="text-lg font-bold">
                Get Support
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="
                  hover:bg-white/20 
                  rounded-full 
                  p-1 
                  transition-colors
                  focus:outline-none 
                  focus:ring-2 
                  focus:ring-white/50
                "
                aria-label="Close support panel"
              >
                <X size={20} />
              </button>
            </div>

            {/* Action Buttons */}
            <div 
              className="p-4 space-y-3"
              style={{
                backgroundColor: '#ffffff',
                opacity: 1,
              }}
            >
              {/* Primary: SMS */}
              <button
                onClick={handleSMS}
                className="
                  w-full
                  bg-green-600
                  hover:bg-green-700
                  text-white
                  font-semibold
                  px-6
                  py-4
                  rounded-lg
                  transition-colors
                  focus:outline-none
                  focus:ring-2
                  focus:ring-green-600
                  focus:ring-offset-2
                  flex
                  items-center
                  justify-center
                  gap-3
                  min-h-[56px]
                  touch-manipulation
                "
                aria-label="Text us (Fastest)"
              >
                <MessageSquare size={20} />
                <span>Text us (Fastest)</span>
              </button>

              {/* Secondary: WhatsApp */}
              <button
                onClick={handleWhatsApp}
                className="
                  w-full
                  bg-[#25D366]
                  hover:bg-[#20BA5A]
                  text-white
                  font-semibold
                  px-6
                  py-4
                  rounded-lg
                  transition-colors
                  focus:outline-none
                  focus:ring-2
                  focus:ring-[#25D366]
                  focus:ring-offset-2
                  flex
                  items-center
                  justify-center
                  gap-3
                  min-h-[56px]
                  touch-manipulation
                "
                aria-label="WhatsApp us"
              >
                <Phone size={20} />
                <span>WhatsApp us</span>
              </button>

              {/* Tertiary: Email */}
              <button
                onClick={handleEmail}
                className="
                  w-full
                  bg-brandred
                  hover:bg-[#E63B3B]
                  text-white
                  font-semibold
                  px-6
                  py-4
                  rounded-lg
                  transition-colors
                  focus:outline-none
                  focus:ring-2
                  focus:ring-brandred
                  focus:ring-offset-2
                  flex
                  items-center
                  justify-center
                  gap-3
                  min-h-[56px]
                  touch-manipulation
                "
                aria-label="Email us"
              >
                <Mail size={20} />
                <span>Email us</span>
              </button>
            </div>

            {/* Footer note */}
            <div 
              className="px-4 pb-4"
              style={{
                backgroundColor: '#ffffff',
                opacity: 1,
              }}
            >
              <p className="text-xs text-gray-500 text-center">
                Choose your preferred contact method
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DirectSupportBubble;

